import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, ClipboardList, Clock, Building2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const emptyForm = {
  name: '',
  branch_ids: [],
  all_services: true,
  service_ids: [],
  unit_type: 'adet',
  inactive_days: '',
  max_amount: '',
  demirbas_statuses: [],
  demirbas_days: '',
};

const DemirbasWarningSettings = ({ readOnly = false }) => {
  const [rules, setRules] = useState([]);
  const [branches, setBranches] = useState([]);
  
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('fortis_token')}`,
    'Content-Type': 'application/json',
  });

  const loadRules = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/purchasing/warning-rules?rule_type=demirbas`, { headers: getAuthHeaders() });
      setRules(res.data || []);
    } catch (e) {
      console.error('Demirbaş uyarı kuralları yüklenemedi:', e);
    }
  }, []);

  const loadBranches = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/branches`, { headers: getAuthHeaders() });
      setBranches(res.data || []);
    } catch (e) {
      console.error('Şubeler yüklenemedi:', e);
    }
  }, []);

  useEffect(() => {
    loadRules();
    loadBranches();
  }, [loadRules, loadBranches]);



  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (rule) => {
    setEditingId(rule.id);
    setForm({
      name: rule.name || '',
      branch_ids: rule.branch_ids || [],
      all_services: rule.all_services !== false,
      service_ids: rule.service_ids || [],
      unit_type: rule.unit_type || 'adet',
      inactive_days: rule.inactive_days ?? '',
      max_amount: rule.max_amount ?? '',
      demirbas_statuses: rule.demirbas_statuses || [],
      demirbas_days: rule.demirbas_days ?? '',
    });
    setModalOpen(true);
  };

  const toggleBranch = (bid) => {
    setForm((prev) => {
      const exists = prev.branch_ids.includes(bid);
      const branch_ids = exists ? prev.branch_ids.filter((x) => x !== bid) : [...prev.branch_ids, bid];
      return { ...prev, branch_ids };
    });
  };

  const toggleService = (sid) => {
    setForm((prev) => {
      const exists = prev.service_ids.includes(sid);
      const service_ids = exists ? prev.service_ids.filter((x) => x !== sid) : [...prev.service_ids, sid];
      return { ...prev, service_ids };
    });
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Lütfen ayar ismi girin');
      return;
    }
    if (form.branch_ids.length === 0) {
      toast.error('En az bir şube seçmelisiniz');
      return;
    }
    if (!form.all_services && form.service_ids.length === 0) {
      toast.error('En az bir ürün seçin veya "Tüm Alınan Hizmetler" seçeneğini işaretleyin');
      return;
    }
    const payload = {
      rule_type: 'demirbas',
      name: form.name.trim(),
      branch_ids: form.branch_ids,
      all_services: form.all_services,
      service_ids: form.all_services ? [] : form.service_ids,
      unit_type: form.unit_type,
      inactive_days: form.inactive_days === '' ? null : Number(form.inactive_days),
      max_amount: form.max_amount === '' ? null : Number(form.max_amount),
      demirbas_statuses: form.demirbas_statuses || [],
      demirbas_days: form.demirbas_days === '' ? null : Number(form.demirbas_days),
    };
    setLoading(true);
    try {
      if (editingId) {
        await axios.put(`${API}/api/purchasing/warning-rules/${editingId}`, payload, { headers: getAuthHeaders() });
        toast.success('Demirbaş uyarı ayarı güncellendi');
      } else {
        await axios.post(`${API}/api/purchasing/warning-rules`, payload, { headers: getAuthHeaders() });
        toast.success('Demirbaş uyarı ayarı oluşturuldu');
      }
      setModalOpen(false);
      loadRules();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kayıt başarısız');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (rule) => {
    if (!window.confirm(`"${rule.name}" ayarını silmek istediğinize emin misiniz?`)) return;
    try {
      await axios.delete(`${API}/api/purchasing/warning-rules/${rule.id}`, { headers: getAuthHeaders() });
      toast.success('Ayar silindi');
      loadRules();
    } catch (e) {
      toast.error('Silme başarısız');
    }
  };

  const branchName = (bid) => branches.find((b) => b.id === bid)?.name || bid;
  const unitSuffix = (t) => ('adet');

  return (
    <div className="space-y-4" data-testid="subcontractor-warning-settings">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Demirbaş Uyarı Ayarları</h3>
          <p className="text-sm text-gray-500">Şube ve demirbaş durumlarına göre uyarı kuralları tanımlayın.</p>
        </div>
        {!readOnly && (
          <Button onClick={openCreate} className="bg-amber-600 hover:bg-amber-700" data-testid="add-subcontractor-rule-btn">
            <Plus className="h-4 w-4 mr-2" /> Yeni Demirbaş Uyarı Ayarı
          </Button>
        )}
      </div>

      {/* Kural Listesi */}
      {rules.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-white py-14 text-center">
          <ClipboardList className="mb-3 h-10 w-10 text-gray-300" />
          <p className="text-base font-medium text-gray-700">Henüz demirbaş uyarı ayarı yok</p>
          <p className="mt-1 text-sm text-gray-400">Yeni bir demirbaş uyarı ayarı ekleyerek başlayın.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="subcontractor-rules-list">
          {rules.map((rule) => (
            <div key={rule.id} className="rounded-xl border border-gray-200 bg-white p-4" data-testid={`subcontractor-rule-${rule.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-800">{rule.name}</span>
                    <Badge variant="outline" className="text-[11px]">
                      <Clock className="h-3 w-3 mr-1" />
                      Demirbaş
                    </Badge>
                  </div>
                  <div className="flex flex-wrap gap-1.5 text-xs">
                    <span className="text-gray-500 flex items-center gap-1"><Building2 className="h-3 w-3" /> Şubeler:</span>
                    {(rule.branch_ids || []).map((bid) => (
                      <Badge key={bid} className="bg-blue-100 text-blue-700 hover:bg-blue-100 text-[11px]">{branchName(bid)}</Badge>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-1.5 text-xs">
                    <span className="text-gray-500 flex items-center gap-1"><ClipboardList className="h-3 w-3" /> Demirbaş Durumları:</span>
                    {rule.all_services ? (
                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[11px]">Tüm Durumlar</Badge>
                    ) : (
                      <span className="text-gray-700">{(rule.demirbas_statuses || []).length} durum</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-4 text-xs text-gray-600 pt-1">
                    <span>Durumlar: <b>{(rule.demirbas_statuses || []).join(', ') || '-'}</b></span>
                    <span>Bekleme Süresi: <b>{rule.demirbas_days ? `${rule.demirbas_days} gün` : '-'}</b></span>
                  </div>
                </div>
                {!readOnly && (
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEdit(rule)} data-testid={`edit-subcontractor-rule-${rule.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleDelete(rule)} data-testid={`delete-subcontractor-rule-${rule.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Ekle/Düzenle Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="inactive-rule-modal">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Demirbaş Uyarı Ayarını Düzenle' : 'Yeni Demirbaş Uyarı Ayarı'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-5">
            {/* İsim */}
            <div className="space-y-2">
              <Label>Ayar İsmi *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="örn. Kritik Malzeme Gecikmesi"
                data-testid="purchase-rule-name"
              />
            </div>

            {/* Şubeler */}
            <div className="space-y-2">
              <Label>Şubeler * <span className="text-xs text-gray-400">(bakılacak şubeleri seçin)</span></Label>
              <div className="border rounded-md p-3 max-h-40 overflow-y-auto grid grid-cols-2 gap-2" data-testid="inactive-rule-branches">
                {branches.map((b) => (
                  <label key={b.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={form.branch_ids.includes(b.id)} onCheckedChange={() => toggleBranch(b.id)} data-testid={`branch-check-${b.id}`} />
                    {b.name}
                  </label>
                ))}
              </div>
              {form.branch_ids.length > 0 && <p className="text-xs text-gray-500">{form.branch_ids.length} şube seçildi</p>}
            </div>

            {/* Alınan Hizmetler */}
                        {/* Demirbaş Durumları */}
            <div className="space-y-3">
              <Label>Hangi durumlarda uyarı verilsin?</Label>
              <div className="grid grid-cols-2 gap-3 border rounded-md p-4">
                {['Depoda', 'Kullanıcıda', 'Zimmette', 'Bozuk', 'Tamirde', 'Kayıp', 'Demirbaş'].map((status) => {
                  const isSelected = form.demirbas_statuses.includes(status);
                  return (
                    <label key={status} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 p-2 rounded transition-colors">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(checked) => {
                          setForm((p) => ({
                            ...p,
                            demirbas_statuses: checked
                              ? [...p.demirbas_statuses, status]
                              : p.demirbas_statuses.filter((s) => s !== status)
                          }));
                        }}
                      />
                      {status}
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Seçili durumlarda kaç gün kalırsa uyarılsın? (Opsiyonel)</Label>
              <Input
                type="number"
                min="1"
                placeholder="Örn: 7"
                value={form.demirbas_days}
                onChange={(e) => setForm({ ...form, demirbas_days: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Vazgeç</Button>
            <Button onClick={handleSave} disabled={loading} className="bg-amber-600 hover:bg-amber-700" data-testid="save-inactive-rule-btn">
              {editingId ? 'Güncelle' : 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DemirbasWarningSettings;
